import {
  getBankStatementsTable,
  postPairBankTransfer,
  postRemoveBankTransfer,
  getIsLockedBankStatementEdit,
} from "@/requests/finances/requestsBankTransfers";

/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { FILTER_NAMES } from "@/static/filterNames";
import { DataOptions } from "vuetify";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { GetBankStatementItem } from "@/models/api/finances/GetBankStatementsResponse";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import i18n from "@/plugins/i18n";
import { filterCapitalize } from "@/filters/capitalize";
import { AlertModelInterface } from "@/models/components/popup/Alert";
import { MESSAGE_TYPES } from "@/static/messageTypes";

/* eslint-disable-next-line */
export interface BankTransferInterface {
  dataTableData: Array<GetBankStatementItem>;
  dataTableOptions: DataOptions;
  countServerSide: number;
  filters: null | Array<CriterionItemRequest>;
}

export const BankTransferState: BankTransferInterface = {
  dataTableData: [],
  dataTableOptions: new DefaultDataOptions(),
  countServerSide: -1,
  filters: null,
};

const BankTransferModule = defineModule({
  state: BankTransferState as BankTransferInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setDataTableData(state, param: Array<GetBankStatementItem>) {
      state.dataTableData = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setCountServerSide(state, param: number) {
      state.countServerSide = param;
    },
    setFilters(state, param: Array<CriterionItemRequest>) {
      state.filters = param;
    },
  },
  actions: {
    async getDataTableData(context) {
      const { commit, state } = _moduleActionContext(context);
      const response = await getBankStatementsTable({
        count: state.dataTableOptions.itemsPerPage,
        page: state.dataTableOptions.page - 1,
        criterions: state.filters,
        filterName: FILTER_NAMES.BankStatementStandardFilter,
      });
      commit.setDataTableData(response.items);
      commit.setCountServerSide(response.totalCount);
    },
    async pairBankTransfer(context, bankStatementId: number) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (
        await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(
              i18n.t("doYouReallyWantToDoActionName", {
                actionName: i18n.t("pair"),
              })
            )
          )
        )
      ) {
        await postPairBankTransfer(bankStatementId);
        // noinspection ES6MissingAwait
        dispatch.getDataTableData();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async removeBankTransfer(context, bankStatementId: number) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();

      if (await dispatch.canUserRemoveBankStatement(bankStatementId)) {
        await postRemoveBankTransfer(bankStatementId);
        // noinspection ES6MissingAwait
        dispatch.getDataTableData();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async canUserRemoveBankStatement(context, bankStatementId: number) {
      const { rootDispatch } = _moduleActionContext(context);
      const isLockedEditResponse = await getIsLockedBankStatementEdit(bankStatementId);
      if (!isLockedEditResponse.isLockedEdit) {
        if (await rootDispatch.popup.addConfirmRemoveElement()) return true;
        else return false;
      }
      if (isLockedEditResponse.isLockedEdit && isLockedEditResponse.userHasPermission) {
        if (
          await rootDispatch.popup.addConfirmRemoveElementWithCustomMessage(
            i18n.t("editThisItemIsLockedButYouHavePermissionToChange") +
              " " +
              filterCapitalize(i18n.t("doYouReallyWantToRemoveElement?"))
          )
        )
          return true;
        else return false;
      }
      rootDispatch.popup.addAlert({
        title: filterCapitalize(i18n.t("error")),
        content: filterCapitalize(i18n.t("noPermissions")),
        type: MESSAGE_TYPES.Error,
      } as AlertModelInterface);
      return false;
    },
  },
});

export default BankTransferModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, BankTransferModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, BankTransferModule);
